import { Check, Copy } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@nucleus-commerce/ui/components/button';
import { toast } from '@nucleus-commerce/ui/components/sonner';

export interface CopyButtonProps {
  value: string;
  /** Accessible name; the visible text is the generic "Copy". */
  label: string;
  size?: 'sm' | 'icon-sm';
  className?: string;
}

/** Writes `value` to the clipboard and shows a tick for a moment; a refused clipboard explains itself. */
export function CopyButton({ value, label, size = 'sm', className }: CopyButtonProps) {
  const { t } = useTranslation('common');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(id);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      toast.error(t('copy.failed'));
    }
  };

  const Icon = copied ? Check : Copy;
  return (
    <Button
      type="button"
      variant="outline"
      size={size}
      className={className}
      aria-label={label}
      onClick={() => void copy()}
    >
      <Icon aria-hidden />
      {size === 'sm' ? (copied ? t('copy.copied') : t('copy.action')) : null}
    </Button>
  );
}
